import type { LintRule } from './types';

/**
 * Core determinism rules for HTML compositions. The render pipeline rasterizes one frame at a
 * time by seeking the composition's timeline to a progress value, so a composition must be a
 * deterministic, seekable pure-function-of-time. These rules reject the constructs that break
 * that contract. They are intentionally a small, extensible starter set — more rules (font
 * inlining, node budget, video handling) can be added as the feature matures.
 */

/**
 * Extract just the CSS text from a composition (the contents of `<style>` blocks and `style="..."`
 * attributes). The animation/transition checks run against this rather than the whole HTML so they
 * don't false-positive on JS object properties of the same name (e.g. a GSAP tween's
 * `{ transition: ... }`), which appear in `<script>` blocks. New regexes are created per call so
 * their `lastIndex` never leaks between invocations.
 */
function extractCssText(html: string): string {
    const parts: string[] = [];
    const styleBlock = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
    const styleAttr = /style\s*=\s*"([^"]*)"|style\s*=\s*'([^']*)'/gi;
    let match: RegExpExecArray | null;
    while ((match = styleBlock.exec(html)) !== null) {
        parts.push(match[1]);
    }
    while ((match = styleAttr.exec(html)) !== null) {
        parts.push(match[1] ?? match[2] ?? '');
    }
    return parts.join('\n');
}

/**
 * CSS animations/transitions run on wall-clock time and cannot be seeked: when the composition is
 * rasterized off-screen they freeze at their starting frame. All motion must come from the
 * seekable timeline instead.
 */
const noCssAnimation: LintRule = {
    id: 'no-css-animation',
    severity: 'error',
    check(html: string): string[] {
        const messages: string[] = [];
        const css = extractCssText(html);
        if (/@(-webkit-)?keyframes\b/i.test(css)) {
            messages.push(
                'CSS @keyframes are not seekable and freeze when the composition is rasterized. Animate via the timeline instead.',
            );
        }
        if (/(?:^|[{;\s])animation(?:-name|-duration)?\s*:/i.test(css)) {
            messages.push(
                'CSS `animation` declarations are not seekable. Animate via the timeline instead.',
            );
        }
        if (/(?:^|[{;\s])transition\s*:/i.test(css)) {
            messages.push(
                'CSS `transition` is driven by wall-clock time and is not seekable. Animate via the timeline instead.',
            );
        }
        return messages;
    },
};

/**
 * An infinitely-repeating timeline has no finite duration, so it cannot be mapped to a normalized
 * progress value. Ambient loops must be expressed as a finite timeline the host repeats.
 */
const noInfiniteTimeline: LintRule = {
    id: 'no-infinite-timeline',
    severity: 'error',
    check(html: string): string[] {
        if (/repeat\s*:\s*(-1|Infinity)/.test(html)) {
            return [
                'An infinitely-repeating timeline (`repeat: -1`) has no finite duration and cannot be seeked. Use a finite timeline.',
            ];
        }
        return [];
    },
};

/**
 * Wall-clock and random sources make a composition non-deterministic, so the same progress value
 * would rasterize to different frames across renders.
 */
const noNonDeterministicSource: LintRule = {
    id: 'no-nondeterministic-source',
    severity: 'error',
    check(html: string): string[] {
        const messages: string[] = [];
        if (/\bDate\.now\s*\(|\bnew\s+Date\s*\(\s*\)/.test(html)) {
            messages.push(
                '`Date.now()` / `new Date()` make the composition non-deterministic. Drive all timing from the timeline.',
            );
        }
        if (/\bMath\.random\s*\(/.test(html)) {
            messages.push(
                '`Math.random()` makes the composition non-deterministic. Use a seeded value instead.',
            );
        }
        if (/\bperformance\.now\s*\(/.test(html)) {
            messages.push(
                '`performance.now()` makes the composition non-deterministic. Drive all timing from the timeline.',
            );
        }
        return messages;
    },
};

/**
 * The host seeks `window.__timelines.main` to drive the composition. Without it the composition
 * can still be rasterized, but only as a static frame, so this is a warning rather than an error.
 */
const requiresTimeline: LintRule = {
    id: 'requires-timeline',
    severity: 'warning',
    check(html: string): string[] {
        if (!/__timelines/.test(html)) {
            return [
                'No `window.__timelines.main` timeline found. The composition will render as a static frame.',
            ];
        }
        return [];
    },
};

/** Open tags of elements marked editable (boolean `data-l5-editable`), with their attribute text. */
function editableOpenTags(html: string): { attrs: string }[] {
    const openTag = /<[a-zA-Z][\w-]*\b([^>]*\bdata-l5-editable(?=[\s/>=])[^>]*)>/gi;
    const tags: { attrs: string }[] = [];
    let match: RegExpExecArray | null;
    while ((match = openTag.exec(html)) !== null) {
        tags.push({ attrs: match[1] });
    }
    return tags;
}

/** The `id` value declared in an element's attribute text, or null when absent/empty. */
function idFromAttrs(attrs: string): string | null {
    const match = /\bid\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(attrs);
    const id = match ? (match[1] ?? match[2] ?? '') : '';
    return id.length > 0 ? id : null;
}

/**
 * Editable text is targeted by its element `id` (`setEditableText(id, …)`), so every editable node
 * needs one.
 */
const editableRequiresId: LintRule = {
    id: 'editable-requires-id',
    severity: 'error',
    check(html: string): string[] {
        const messages: string[] = [];
        for (const { attrs } of editableOpenTags(html)) {
            if (!idFromAttrs(attrs)) {
                messages.push(
                    'A `data-l5-editable` element has no `id`. Editable text is targeted by its id, so each editable node needs a unique id.',
                );
            }
        }
        return messages;
    },
};

/** Two editable nodes sharing an id are an ambiguous edit target. */
const editableUniqueId: LintRule = {
    id: 'editable-unique-id',
    severity: 'error',
    check(html: string): string[] {
        const counts = new Map<string, number>();
        for (const { attrs } of editableOpenTags(html)) {
            const id = idFromAttrs(attrs);
            if (id) {
                counts.set(id, (counts.get(id) ?? 0) + 1);
            }
        }
        const messages: string[] = [];
        for (const [id, count] of counts) {
            if (count > 1) {
                messages.push(
                    `Duplicate id="${id}" among editable nodes (${count}). Each editable node needs a unique id so its text can be targeted.`,
                );
            }
        }
        return messages;
    },
};

/**
 * Natural text editing replaces an editable node's text as a single plain text node. Child elements
 * mean the content is composed/animated/split — not safely editable in place — so warn (rather than
 * error: it still renders, just isn't offered for NTE).
 */
const editableTextOnly: LintRule = {
    id: 'editable-text-only',
    severity: 'warning',
    check(html: string): string[] {
        const editableElement =
            /<([a-zA-Z][\w-]*)\b[^>]*\bdata-l5-editable(?=[\s/>=])[^>]*>([\s\S]*?)<\/\1>/gi;
        const messages: string[] = [];
        let match: RegExpExecArray | null;
        while ((match = editableElement.exec(html)) !== null) {
            if (/<[a-zA-Z]/.test(match[2])) {
                messages.push(
                    'A `data-l5-editable` element contains child elements; editable text must be a single plain text node (animated/split markup is not safely editable).',
                );
            }
        }
        return messages;
    },
};

export const CORE_LINT_RULES: LintRule[] = [
    noCssAnimation,
    noInfiniteTimeline,
    noNonDeterministicSource,
    requiresTimeline,
    editableRequiresId,
    editableUniqueId,
    editableTextOnly,
];
